import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import DropdownChevron from "./dropdown-chevron";

interface MultiSelectDropdownProps {
  options: Array<string | { value: string; label: string }>;
  selected: string[];
  onToggle: (val: string) => void;
  placeholder?: string;
  /** Show search field and type-to-filter when the list is open. Default true. */
  searchable?: boolean;
}

export default function MultiSelectDropdown({
  options,
  selected,
  onToggle,
  placeholder = "Select options",
  searchable = true,
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const normalizedOptions = useMemo(
    () =>
      options.map((option) =>
        typeof option === "string" ? { value: option, label: option } : option
      ),
    [options]
  );

  const optionLabelMap = useMemo(
    () => new Map(normalizedOptions.map((option) => [option.value, option.label])),
    [normalizedOptions]
  );

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchQuery("");
  }, []);

  const filteredOptions = useMemo(() => {
    if (!searchable) return normalizedOptions;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return normalizedOptions;
    return normalizedOptions.filter((option) =>
      option.label.toLowerCase().includes(q)
    );
  }, [normalizedOptions, searchQuery, searchable]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeDropdown]);

  useEffect(() => {
    if (!isOpen || !searchable) return;
    const id = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [isOpen, searchable]);

  useEffect(() => {
    if (!isOpen || !searchable) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeDropdown();
        return;
      }

      const target = event.target as HTMLElement;
      if (target === searchInputRef.current) return;
      if (
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA") &&
        !containerRef.current?.contains(target)
      ) {
        return;
      }

      if (
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        event.preventDefault();
        searchInputRef.current?.focus();
        setSearchQuery((prev) => prev + event.key);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, searchable, closeDropdown]);

  return (
    <div className="relative" ref={containerRef}>
      <div
        className="relative flex bg-[#F9F9F9] px-4 py-3.5 gap-2 cursor-pointer min-h-[46px] rounded-md text-sm items-center justify-between shadow-sm border-2 border-transparent hover:border-lime-400 focus:outline-none focus:ring-4 focus:ring-lime-400 focus:border-lime-400 transition-all duration-200 group"
        onClick={() => {
          if (isOpen) closeDropdown();
          else setIsOpen(true);
        }}
      >
        <div className="flex flex-wrap gap-2">
          {selected.length > 0 ? (
            selected.map((item) => (
              <span
                key={item}
                className="bg-black text-white text-[11px] px-3 rounded-md font-medium flex items-center gap-1.5"
              >
                {optionLabelMap.get(item) || item}
                <X
                  className="w-3 h-3 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggle(item);
                  }}
                />
              </span>
            ))
          ) : (
            <span className="text-gray-400 text-sm ml-2">{placeholder}</span>
          )}
        </div>
        <div className="flex-1"></div>
        <DropdownChevron isOpen={isOpen} />
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
          {searchable && (
            <div
              className="sticky top-0 z-10 border-b border-gray-200 bg-white p-2"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Type to search..."
                  className="w-full rounded-md border border-gray-200 bg-[#F9F9F9] py-2 pl-9 pr-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-lime-400 focus:outline-none focus:ring-2 focus:ring-lime-400"
                  aria-label="Search options"
                />
              </div>
            </div>
          )}

          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500">No results found</div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-[#ABD405] transition-colors ${selected.includes(option.value) ? "bg-[#E6EDDD]" : ""}`}
                  onClick={() => onToggle(option.value)}
                >
                  <div
                    className={`w-4 h-4 border rounded-md flex items-center justify-center ${selected.includes(option.value) ? "border-[#E6EDDD] bg-white" : "border-gray-300"}`}
                  >
                    {selected.includes(option.value) && (
                      <div className="w-2 h-2 bg-[#E6EDDD] rounded-md" />
                    )}
                  </div>
                  <span className="text-sm text-gray-700">{option.label}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
